"use client";

import "react-international-phone/style.css";

import {
  FlagImage,
  defaultCountries,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";

import Button from "@/components/library/Button";
import type { CompanyInfo } from "@/app/api/company-lookup/route";
import Image from "next/image";
import clsx from "clsx";
import { createPortal } from "react-dom";
import { useLocalePath } from "@/hooks/useLocalePath";
import { useReCaptcha } from "next-recaptcha-v3";

interface CompanyCardProps {
  companyInfo: CompanyInfo | null;
  isLoading: boolean;
  domain?: string | null;
}

/* ═══════════════════════ LOADING STATE ═══════════════════════ */

const SCAN_MESSAGES = [
  { key: "scanning", fallback: "Scanning {domain}" },
  { key: "learning", fallback: "Analyzing products, team size & use cases" },
  { key: "crafting", fallback: "Crafting your personalized strategy" },
];

const LoadingState = ({ domain }: { domain?: string | null }) => {
  const { t } = useTranslation();
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex((prev) =>
        prev < SCAN_MESSAGES.length - 1 ? prev + 1 : prev,
      );
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const msg = SCAN_MESSAGES[msgIndex];
  const label = t(`hero.card.loading.${msg.key}`).replace(
    "{domain}",
    domain || "...",
  );

  return (
    <div className="text-center py-4">
      <div className="relative w-[96px] h-[96px] mx-auto mb-6">
        <svg
          width="96"
          height="96"
          viewBox="0 0 76 76"
          className="absolute inset-0 w-full h-full"
        >
          <circle
            cx="38"
            cy="38"
            r="32"
            fill="none"
            className="stroke-gail-primary/12"
            strokeWidth="4"
          />
          <circle
            cx="38"
            cy="38"
            r="32"
            fill="none"
            className="stroke-gail-primary origin-center animate-spin"
            strokeWidth="4"
            strokeDasharray="56 145"
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[48px] h-[48px] rounded-[12px] flex items-center justify-center bg-gail-primary">
          <Image
            src="/images/gail_icon_black.svg"
            alt="Gail"
            width={24}
            height={24}
            className="invert brightness-200"
          />
        </div>
      </div>

      <div className="text-[18px] sm:text-[20px] font-semibold text-foreground mb-1.5">
        {msgIndex === 0 ? (
          <>
            Scanning{" "}
            <span className="text-gail-primary">{domain || "..."}</span>
          </>
        ) : (
          label
        )}
      </div>
      <p className="text-[14px] sm:text-[15px] text-black/40 mb-5">
        {msgIndex === 0
          ? t("hero.card.loading.learning")
          : t("hero.card.loading.scanSub")}
      </p>

      <div className="h-[3px] bg-black/8 rounded-full overflow-hidden mx-auto max-w-[200px]">
        <div
          className="h-full rounded-full transition-all duration-3000 ease-out bg-gail-primary"
          style={{
            width: `${Math.min(25 + msgIndex * 28, 85)}%`,
          }}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════ GAIL CAN DO ICONS ═══════════════════════ */

const GailCanDoIcon = ({ index }: { index: number }) => {
  const icons = [
    <svg
      key="phone"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>,
    <svg
      key="crm"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>,
    <svg
      key="doc"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>,
  ];
  return icons[index % icons.length];
};

/* ═══════════════════════ COUNTRY SELECTOR (PORTAL) ═══════════════════════ */

const parsedCountries = defaultCountries.map((c) => parseCountry(c));

const PortalCountrySelector = ({
  selectedIso2,
  onSelect,
}: {
  selectedIso2: string;
  onSelect: (iso2: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const posRef = useRef({ top: 0, left: 0 });
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const getPos = useCallback(() => {
    if (!btnRef.current) return posRef.current;
    const rect = btnRef.current.getBoundingClientRect();
    const next = { top: rect.bottom + 4, left: rect.left };
    posRef.current = next;
    return next;
  }, []);

  const handleToggle = useCallback(() => {
    setSearch("");
    const nextPos = getPos();
    setPos(nextPos);
    setOpen((v) => !v);
  }, [getPos]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => searchRef.current?.focus(), 0);

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      if (btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const handleScroll = () => setPos(getPos());

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open, getPos]);

  const filtered = search
    ? parsedCountries.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase()),
      )
    : parsedCountries;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-1 pl-3 pr-1 py-2.5 cursor-pointer shrink-0"
      >
        <FlagImage iso2={selectedIso2} size="20px" />
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={clsx("transition-transform", open && "rotate-180")}
        >
          <path
            d="M2 4l3 3 3-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="bg-white rounded-xl shadow-2xl border border-black/10 overflow-hidden font-monument"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              zIndex: 99999,
              width: 280,
            }}
          >
            <div className="p-2 border-b border-black/5">
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search countries..."
                className="w-full px-2.5 py-1.5 text-base rounded-lg border border-black/10 focus:outline-none focus:border-gail-primary/30 bg-white"
              />
            </div>
            <div className="max-h-[200px] overflow-y-auto">
              {filtered.map((c) => (
                <button
                  key={c.iso2}
                  type="button"
                  onClick={() => {
                    onSelect(c.iso2);
                    setOpen(false);
                  }}
                  className={clsx(
                    "flex items-center gap-2.5 w-full px-3 py-2 text-[12px] hover:bg-black/5 cursor-pointer transition-colors",
                    c.iso2 === selectedIso2 && "bg-gail-primary/5 font-medium",
                  )}
                >
                  <FlagImage iso2={c.iso2} size="18px" />
                  <span className="truncate">{c.name}</span>
                  <span className="text-black/40 ml-auto shrink-0">
                    +{c.dialCode}
                  </span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-4 text-[12px] text-black/40 text-center">
                  No countries found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

/* ═══════════════════════ CALL FLOW ═══════════════════════ */

const CallFlow = ({
  companyInfo,
  onCallSuccess,
}: {
  companyInfo: CompanyInfo;
  onCallSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const recaptcha = useReCaptcha();

  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [firstName, setFirstName] = useState(
    companyInfo.inferredFirstName || "",
  );
  const [isApproved, setIsApproved] = useState(false);
  const [callStatus, setCallStatus] = useState<
    "idle" | "calling" | "success" | "error"
  >("idle");
  const [phoneError, setPhoneError] = useState(false);
  const [consentError, setConsentError] = useState(false);

  const {
    inputValue: phoneInputValue,
    phone: e164Phone,
    country,
    setCountry,
    handlePhoneValueChange,
    inputRef: phoneInputRef,
  } = usePhoneInput({
    defaultCountry: "us",
    value: "",
    onChange: () => {
      if (phoneError) setPhoneError(false);
    },
  });

  const handleInitiateCall = async () => {
    if (callStatus === "calling") return;
    let hasValidationError = false;
    if (!e164Phone || e164Phone.length < 5) {
      setPhoneError(true);
      setTimeout(() => setPhoneError(false), 600);
      hasValidationError = true;
    }
    if (!isApproved) {
      setConsentError(true);
      setTimeout(() => setConsentError(false), 600);
      hasValidationError = true;
    }
    if (hasValidationError) return;
    if (!recaptcha.loaded) return;
    setCallStatus("calling");
    try {
      const token = await recaptcha.executeRecaptcha("personalized_call");
      const response = await fetch("/api/personalized-call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: e164Phone,
          firstName: firstName.trim() || undefined,
          companyInfo,
          recaptchaToken: token,
        }),
      });
      if (!response.ok) throw new Error("Call initiation failed");
      setCallStatus("success");
      onCallSuccess();
    } catch (error) {
      console.error("Call error:", error);
      setCallStatus("error");
    }
  };

  if (callStatus === "success") {
    return null;
  }

  if (callStatus === "error") {
    return (
      <div className="mt-5 text-center">
        <div className="text-sm text-red-500 mb-3">
          {t("hero.card.callError")}
        </div>
        <button
          onClick={() => setCallStatus("idle")}
          className="text-sm underline hover:text-gray-700 transition-colors text-gail-primary"
        >
          {t("hero.tryAgain")}
        </button>
      </div>
    );
  }

  if (!showPhoneInput) {
    return (
      <div className="animate-hero-card-enter-delay-3 mt-3 sm:mt-4 text-center">
        <button
          type="button"
          onClick={() => setShowPhoneInput(true)}
          className="text-sm text-black/45 underline underline-offset-2 hover:text-black/70 transition-colors"
        >
          {t("hero.card.callPrompt")}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <div className="flex gap-2 animate-[fadeSlideIn_0.3s_ease-out_both]">
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder={t("hero.card.firstNamePlaceholder")}
          className="w-[110px] shrink-0 px-3 py-2 text-base rounded-xl border border-black/10 focus:outline-none focus:ring-2 focus:ring-gail-primary/20 focus:border-gail-primary/30 bg-white transition-all"
        />
        <div
          className={clsx(
            "flex-1 flex items-center rounded-xl border bg-white transition-all min-w-0",
            phoneError
              ? "border-red-400 ring-2 ring-red-200/40 animate-hero-shake"
              : "border-black/10 focus-within:ring-2 focus-within:ring-gail-primary/20 focus-within:border-gail-primary/30",
          )}
        >
          <PortalCountrySelector
            selectedIso2={country.iso2}
            onSelect={(iso2) => setCountry(iso2, { focusOnInput: true })}
          />
          <input
            ref={phoneInputRef}
            type="tel"
            value={phoneInputValue}
            onChange={handlePhoneValueChange}
            placeholder={t("hero.card.phonePlaceholder")}
            className="flex-1 px-1 py-2 text-base bg-transparent focus:outline-none min-w-0"
          />
        </div>
      </div>
      <div
        className={clsx(
          "flex items-start gap-2 animate-[fadeSlideIn_0.3s_ease-out_0.1s_both]",
          consentError && "animate-hero-shake",
        )}
      >
        <input
          type="checkbox"
          id="callConsent"
          checked={isApproved}
          onChange={(e) => {
            setIsApproved(e.target.checked);
            if (consentError) setConsentError(false);
          }}
          className={clsx(
            "mt-0.5 shrink-0 rounded accent-gail-primary",
            consentError && "ring-2 ring-red-300",
          )}
        />
        <label
          htmlFor="callConsent"
          className={clsx(
            "text-sm leading-relaxed",
            consentError ? "text-red-500" : "text-black/40",
          )}
        >
          <Trans i18nKey="hero.card.consent">
            By checking this box, I agree to receive a call from Gail AI to the
            provided phone number. View our{" "}
            <a href={lp("/terms-conditions")} className="underline">
              Terms
            </a>
            {" & "}
            <a href={lp("/privacy-policy")} className="underline">
              Privacy
            </a>
            <span>.</span>
          </Trans>
        </label>
      </div>
      <div className="animate-[fadeSlideIn_0.3s_ease-out_0.2s_both]">
        <Button
          onClick={handleInitiateCall}
          wide
          className="flex! items-center justify-center"
          iconBefore={
            callStatus === "calling" ? (
              <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin align-middle" />
            ) : (
              <PhoneIcon
                className="w-4 h-4 inline-block align-middle"
                style={{ color: "white" }}
              />
            )
          }
        >
          {callStatus === "calling"
            ? t("hero.card.callingYou")
            : t("hero.card.callMeNow")}
        </Button>
      </div>
    </div>
  );
};

/* ═══════════════════════ CARD SHELL ═══════════════════════ */

const CompanyCard = ({ companyInfo, isLoading, domain }: CompanyCardProps) => {
  return (
    <div
      className={clsx(
        "bg-white/97 backdrop-blur-xl",
        "rounded-2xl",
        "p-5 sm:p-6 md:p-8",
        "w-full",
        "shadow-xl",
        "flex flex-col",
        "font-monument",
      )}
    >
      {isLoading && (
        <div className="w-full flex items-center justify-center aspect-1622/1844">
          <LoadingState domain={domain} />
        </div>
      )}
      {!isLoading && companyInfo && <ActiveCard companyInfo={companyInfo} />}
      {!isLoading && !companyInfo && (
        <div className="w-full flex items-center justify-center aspect-1622/1844">
          <LoadingState domain={domain} />
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════ ACTIVE CARD ═══════════════════════ */

const ActiveCard = ({ companyInfo }: { companyInfo: CompanyInfo }) => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const [callSuccess, setCallSuccess] = useState(false);

  if (callSuccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center animate-hero-card-enter">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-gail-primary/10 border-[0.5px] border-gail-primary/25">
          <PhoneIcon className="w-6 h-6 text-gail-primary" />
        </div>
        <h2 className="text-lg font-bold text-foreground mb-2">
          {t("hero.card.callConfirmTitle")}
        </h2>
        <p className="text-[13px] text-black/50 leading-relaxed max-w-xs mb-8">
          {t("hero.card.callConfirmDesc", { company: companyInfo.companyName })}
        </p>
        <div className="w-full max-w-sm rounded-2xl border border-black/8 bg-black/2 px-4 py-5 text-center">
          <p className="text-[16px] font-semibold text-foreground">
            {t("hero.card.accountReadyTitle")}
          </p>
          <p className="text-[13px] text-black/55 leading-relaxed mb-4">
            {t("hero.card.accountReadyBody")}
          </p>
          <Button href={lp("/signup")} wide className="text-sm!">
            {t("hero.card.createAccount")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Company name header */}
      <div className="mb-3 sm:mb-4 animate-hero-card-enter">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground leading-tight text-left">
          {companyInfo.companyName}
        </h2>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:gap-y-3 mb-3 sm:mb-4 animate-hero-card-enter-delay-1 text-left">
        <InfoField
          label={t("hero.card.summary")}
          value={companyInfo.keyProducts}
        />
        <InfoField
          label={t("hero.card.hqLocations")}
          value={companyInfo.headquarters}
        />
        <InfoField
          label={t("hero.card.keyProducts")}
          value={companyInfo.targetMarket}
        />
        <InfoField
          label={t("hero.card.companySizeHistory")}
          value={companyInfo.companySize}
        />
      </div>

      {/* What Gail can do */}
      <div className="animate-hero-card-enter-delay-2 flex-1">
        <h3 className="text-base sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 text-left text-gail-primary">
          {t("hero.card.whatGailCanDo")}
        </h3>
        <div className="space-y-1.5 sm:space-y-2">
          {companyInfo.gailCanDo.map((item, idx) => (
            <div
              key={item}
              className="flex items-center gap-2.5 sm:gap-3 px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg border border-black/6 bg-white"
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center shrink-0 text-black/60">
                <GailCanDoIcon index={idx} />
              </div>
              <span className="text-[11px] sm:text-[13px] md:text-[14px] text-black/70 leading-snug text-left">
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="animate-hero-card-enter-delay-3 mt-4 sm:mt-5 rounded-2xl border border-black/8 bg-black/2 px-4 py-4 text-center">
        <p className="text-[16px] font-semibold text-foreground">
          {t("hero.card.accountReadyTitle")}
        </p>
        <p className="text-[13px] text-black/55 leading-relaxed mb-4">
          {t("hero.card.accountReadyBody")}
        </p>
        <Button href={lp("/signup")} wide className="text-sm!">
          {t("hero.card.createAccount")}
        </Button>
      </div>

      <CallFlow
        companyInfo={companyInfo}
        onCallSuccess={() => setCallSuccess(true)}
      />
    </div>
  );
};

const InfoField = ({ label, value }: { label: string; value: string }) => (
  <div className="text-left">
    <label className="block text-[9px] sm:text-[10px] uppercase tracking-[0.8px] text-black/40 font-medium mb-0.5 sm:mb-1">
      {label}
    </label>
    <p className="text-[11px] sm:text-[13px] md:text-[15px] text-black/75 leading-snug">
      {value}
    </p>
  </div>
);

const PhoneIcon = ({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <svg className={className} style={style} viewBox="0 0 20 20" fill="none">
    <path
      d="M4.5 2.5C4.5 2.5 3 2.5 3 4s1 3.5 3 5.5S10 13 11.5 13.5s2.5.5 2.5.5.5-1 .5-1.5-.5-1-1.5-1.5-1.5-.5-1.5-.5-.5.5-1 .5-3-2-3-2-.5-.5 0-1 1-1 1-1-1.5-2-2-2.5S4.5 2.5 4.5 2.5z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);

export default CompanyCard;
